import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowRight, Eye, EyeOff, Smartphone, ChevronLeft } from 'lucide-react';
import Logo from '../components/Logo';
import { Button, Field, Input } from '../components/UI';
import { useAuth, HOME_FOR } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

export default function Login() {
  const { login, requestOtp, verifyOtp, user } = useAuth();
  const nav = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState({ email: '', password: '' });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // One sign-in page for every role: email + password, or mobile + WhatsApp code.
  const [method, setMethod] = useState('email');      // email | mobile
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  const switchMethod = (m) => { setMethod(m); setError(''); setOtpSent(false); setOtp(''); };

  const sendCode = async (e) => {
    e.preventDefault();
    setError('');
    const digits = mobile.replace(/\D/g, '');
    if (digits.length < 10) return setError('Enter your 10-digit mobile number');
    setBusy(true);
    try {
      const data = await requestOtp(digits);
      setOtpSent(true);
      // Development servers return the code so this is testable without WhatsApp.
      if (data?.devOtp) toast.info(`Development code: ${data.devOtp}`);
      else toast.success('Verification code sent on WhatsApp');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.replace(/\D/g, '').length < 6) return setError('Enter the 6-digit code');
    setBusy(true);
    try {
      const signedIn = await verifyOtp(mobile.replace(/\D/g, ''), otp.replace(/\D/g, ''));
      toast.success(`Welcome, ${signedIn.name.split(' ')[0]}`);
      nav(HOME_FOR[signedIn.role] || '/', { replace: true });
    } catch (err) {
      setError(err.message);
      setOtp('');
    } finally {
      setBusy(false);
    }
  };

  if (user) return <Navigate to={HOME_FOR[user.role] || '/admin'} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password) return setError('Please enter your login id and password');

    setBusy(true);
    try {
      const signedIn = await login(form.email.trim(), form.password);
      toast.success(`Welcome back, ${signedIn.name.split(' ')[0]}`);
      nav(HOME_FOR[signedIn.role] || '/admin', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', position: 'relative', zIndex: 1 }}>
      {/* ── brand panel ─────────────────────────────────────────────── */}
      <div className="gl-brand-panel" style={{
        flex: '1 1 52%', position: 'relative', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        padding: '52px 56px',
        background: 'linear-gradient(155deg, #0E0E11 0%, #08080A 55%, #0B0B0C 100%)',
        borderRight: '1px solid var(--border)',
      }}>
        {/* slow-drifting gold aura */}
        <motion.div
          animate={{ scale: [1, 1.14, 1], opacity: [0.5, 0.78, 0.5] }}
          transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', top: '16%', left: '18%', width: 480, height: 480,
            background: 'radial-gradient(circle, rgba(239,212,138,0.14), transparent 68%)',
            filter: 'blur(52px)', pointerEvents: 'none',
          }}
        />

        <Logo size={38} showText />

        <div style={{ position: 'relative', maxWidth: 520 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.86 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            style={{ marginBottom: 34 }}
          >
            <motion.div
              animate={{ y: [0, -11, 0] }}
              transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Logo size={124} mark />
            </motion.div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            style={{
              fontFamily: 'var(--font-display)', fontSize: 46, lineHeight: 1.14,
              fontWeight: 600, letterSpacing: '-0.01em',
            }}
          >
            Every detail of your event,{' '}
            <span style={{
              background: 'linear-gradient(120deg, var(--gold-200), var(--gold-500))',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              in one place.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28, duration: 0.6 }}
            style={{ color: 'var(--text-mid)', fontSize: 15, marginTop: 18, lineHeight: 1.7 }}
          >
            Flights, hotels, rooms, coaches, luggage and attendance — coordinated
            across every attendee, from invitation to return journey.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.45 }}
            style={{ display: 'flex', gap: 30, marginTop: 40, flexWrap: 'wrap' }}
          >
            {[
              ['QR', 'Digital identity'],
              ['24/7', 'Live tracking'],
              ['1 tap', 'WhatsApp updates'],
            ].map(([big, small]) => (
              <div key={small}>
                <div style={{ fontSize: 21, fontWeight: 700, color: 'var(--gold-400)', fontFamily: 'var(--font-display)' }}>{big}</div>
                <div style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 2 }}>{small}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <p style={{ fontSize: 11.5, color: 'var(--text-lo)', letterSpacing: 0.4 }}>
          MICE Event Management &amp; Corporate Travel Automation
        </p>
      </div>

      {/* ── form panel ──────────────────────────────────────────────── */}
      <div style={{
        flex: '1 1 48%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 28px',
      }}>
        <motion.div
          initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: '100%', maxWidth: 384 }}
        >
          <div className="gl-mobile-logo" style={{ display: 'none', justifyContent: 'center', marginBottom: 28 }}>
            <Logo size={54} mark />
          </div>

          <h2 style={{ fontSize: 25, fontWeight: 600, letterSpacing: '-0.02em' }}>Sign in</h2>
          <p style={{ color: 'var(--text-mid)', fontSize: 13.5, marginTop: 7, marginBottom: 22 }}>
            One sign-in for admins, company teams, hotels, event staff and attendees.
          </p>

          <div role="tablist" style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, padding: 4, marginBottom: 22,
            background: 'var(--ink-850)', border: '1px solid var(--border)', borderRadius: 'var(--r)',
          }}>
            {[['email', 'Login & password', Mail], ['mobile', 'WhatsApp code', Smartphone]].map(([key, text, Icon]) => (
              <button key={key} type="button" role="tab" aria-selected={method === key}
                onClick={() => switchMethod(key)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  padding: '9px 8px', borderRadius: 'var(--r-sm)', fontSize: 12.8, fontWeight: 600,
                  color: method === key ? 'var(--ink-900)' : 'var(--text-mid)',
                  background: method === key ? 'var(--gold-400)' : 'transparent',
                  transition: 'background 0.18s, color 0.18s',
                }}>
                <Icon size={14} /> {text}
              </button>
            ))}
          </div>

          {method === 'mobile' ? (
            <form onSubmit={otpSent ? confirmCode : sendCode} style={{ display: 'flex', flexDirection: 'column', gap: 17 }}>
              {!otpSent ? (
                <Field label="Mobile number" hint="The number your event coordinator registered">
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--gold-400)', fontSize: 13.5, fontWeight: 600 }}>+91</span>
                    <Input
                      type="tel" inputMode="numeric" autoComplete="tel" placeholder="98765 43210"
                      value={mobile} autoFocus
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      style={{ paddingLeft: 48, letterSpacing: 1 }}
                    />
                  </div>
                </Field>
              ) : (
                <>
                  <button type="button" onClick={() => { setOtpSent(false); setOtp(''); setError(''); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-mid)', fontSize: 12.8, alignSelf: 'flex-start' }}>
                    <ChevronLeft size={15} /> Change number
                  </button>
                  <Field label={`Code sent to +91 ${mobile}`}>
                    <Input
                      inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code"
                      value={otp} autoFocus
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      style={{ letterSpacing: 6, fontSize: 18, textAlign: 'center' }}
                    />
                  </Field>
                </>
              )}

              {error && (
                <div style={{
                  padding: '10px 13px', background: 'var(--danger-dim)',
                  border: '1px solid rgba(248,113,113,0.24)', borderRadius: 'var(--r)',
                  color: 'var(--danger)', fontSize: 12.8,
                }}>
                  {error}
                </div>
              )}

              <Button type="submit" size="lg" loading={busy} style={{ marginTop: 4 }}>
                {busy ? (otpSent ? 'Verifying' : 'Sending code') : (otpSent ? 'Verify & sign in' : 'Send code on WhatsApp')}
                {!busy && <ArrowRight size={16} />}
              </Button>
            </form>
          ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 17 }}>
            <Field label="Email or mobile number">
              <div style={{ position: 'relative' }}>
                <Mail size={15} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-lo)' }} />
                <Input
                  type="text" autoComplete="username" placeholder="you@company.com or 98765 43210"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  style={{ paddingLeft: 37 }}
                />
              </div>
            </Field>

            <Field label="Password">
              <div style={{ position: 'relative' }}>
                <Lock size={15} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-lo)' }} />
                <Input
                  type={show ? 'text' : 'password'} autoComplete="current-password" placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  style={{ paddingLeft: 37, paddingRight: 40 }}
                />
                <button type="button" onClick={() => setShow((v) => !v)}
                  aria-label={show ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-lo)', display: 'flex' }}>
                  {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>

            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                style={{
                  padding: '10px 13px', background: 'var(--danger-dim)',
                  border: '1px solid rgba(248,113,113,0.24)', borderRadius: 'var(--r)',
                  color: 'var(--danger)', fontSize: 12.8,
                }}
              >
                {error}
              </motion.div>
            )}

            <Button type="submit" size="lg" loading={busy} style={{ marginTop: 4 }}>
              {busy ? 'Signing in' : 'Sign in'} {!busy && <ArrowRight size={16} />}
            </Button>
          </form>
          )}

          {method === 'email' && (
          <div style={{ marginTop: 26, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            <p style={{ fontSize: 11.5, color: 'var(--text-lo)', lineHeight: 1.6 }}>
              Attendees and staff can also use the Mobile number tab to sign in
              with a WhatsApp code.
            </p>
          </div>
          )}
        </motion.div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .gl-brand-panel { display: none !important; }
          .gl-mobile-logo { display: flex !important; }
        }
      `}</style>
    </div>
  );
}
